// AIAssistant uses dynamic message parsing from web worker with varied formats
// that cannot be statically typed. Using 'any' is appropriate for this use case.
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { FeatureLock } from '@/components/legacy-ui/FeatureLock';
import { createContactSupportButton } from '@/components/legacy-ui/utils';
import { Spinner } from '@/components/ui/spinner';
import { useAIAgents } from '@/hooks/useAIAgents';
import {
  useGetConversationById,
  useGetConversationTraces,
} from '@/hooks/useChat';
import { useChatWorker } from '@/hooks/useChatWorker';
import { useConversationRoot } from '@/hooks/useConversationRoot';
import { useDescendantConversations } from '@/hooks/useDescendantConversations';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { useFlag } from '@/hooks/useFlags';
import {
  type SubmitCredential,
  useInteractions,
} from '@/hooks/useInteractions';
import { countLedgerJobs } from '@/sections/insights/aiAssistant/utils/jobLedger.util';
import { isLastPendingRequest } from '@/sections/insights/aiAssistant/utils/pendingRequests.util';
import { useCreateHunt, useHunts } from '@/sections/insights/hunts/useHunts';
import { useAuth } from '@/state/auth';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

import { HuntStatusDrawer } from './components/AutoDrawer';
import { BudgetExhaustedModal } from './components/BudgetExhaustedModal';
import { CredentialRequestsDrawer } from './components/CredentialRequestsDrawer';
import { HeaderBar } from './components/HeaderBar';
import { InputForm } from './components/InputForm';
import { LaunchHannibalDialog } from './components/LaunchHannibalDialog';
import { MessageArea } from './components/MessageArea';
import { Sidebar } from './components/Sidebar';
import { TraceDrawer } from './components/TraceDrawer';
import { WelcomeView } from './components/WelcomeView';
import {
  clampAgentToMode,
  ConvState,
  deriveConvState,
} from './config/deriveConvState';
import { saveConversationMode } from './config/storage';
import { MAX_TEXTAREA_HEIGHT } from './constants/limits';
import { useAdvanceCredentialSelection } from './hooks/useAdvanceCredentialSelection';
import { useAgentExecution } from './hooks/useAgentExecution';
import { useAttackGraphSaveInvalidation } from './hooks/useAttackGraphSaveInvalidation';
import { useAutoOpenCredentialDrawer } from './hooks/useAutoOpenCredentialDrawer';
import { useConversationActiveJobs } from './hooks/useConversationActiveJobs';
import { useConversationManager } from './hooks/useConversationManager';
import { useConversationSwitch } from './hooks/useConversationSwitch';
import { useHannibalSummonGate } from './hooks/useHannibalSummonGate';
import { useHuntMode } from './hooks/useHuntMode';
import { useInteractionToast } from './hooks/useInteractionToast';
import { useMessageHistory } from './hooks/useMessageHistory';
import { useMessageSending } from './hooks/useMessageSending';
import { usePlannerStaleCutoff } from './hooks/usePlannerStaleCutoff';
import { usePopoverDismiss } from './hooks/usePopoverDismiss';
import { useScrollBehavior } from './hooks/useScrollBehavior';
import { useSummon } from './hooks/useSummon';
import { useTraceDrawerAutoOpen } from './hooks/useTraceDrawerAutoOpen';
import { useVoiceInput } from './hooks/useVoiceInput';
import { useAutoDrawerStore } from './stores/autoDrawer.store';
import { useTraceDrawerStore } from './stores/traceDrawer.store';
import type { AIAssistantMode, SidebarTab } from './types';
import { useCommandMenu } from './useCommandMenu';
import { mergeSelectedConversation } from './utils/selectConversation';
import { parseSubagentTopic } from './utils/subagentTopic.util';

export function AIAssistant() {
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationId = searchParams.get('conversationId');
  const summonMessage = searchParams.get('summonMessage');
  const summonMode = searchParams.get('summonMode') as 'query' | 'agent' | null;

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useStorage(
    { key: StorageKey.AI_ASSISTANT_SIDEBAR_COLLAPSED },
    true
  );
  const [activeTab, setActiveTab] = useState<SidebarTab>('conversations');

  const [newMessage, setNewMessage] = useState('');

  // modeChangeRef is owned here and threaded into useConversationManager
  // (writer) and useConversationSwitch (reader). It carries the new-chat hunt
  // ('auto') intent for the switch effect only — convState derivation is
  // deliberately ref-free (see deriveConvState).
  const modeChangeRef = useRef<'auto' | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const commandMenuRef = useRef<HTMLDivElement>(null);

  const { isPraetorianUser, whoami } = useAuth();
  const {
    locked: aiAssistantLocked,
    lockTitle,
    lockDescription,
    featureName,
    isLoading: featureLockLoading,
  } = useFeatureLock('aiAssistant');
  const { disableAIInference, enableAIAgents, huntVisibility } = useFlag();

  const isAIDisabledButBypassed = disableAIInference && !aiAssistantLocked;
  const { data: agents } = useAIAgents();
  const { data: hunts = [] } = useHunts();

  const isAtLeastAnalyst =
    whoami?.role === 'analyst' || whoami?.role === 'admin';
  const canUseAgentMode =
    (isPraetorianUser && isAtLeastAnalyst) ||
    (enableAIAgents && isAtLeastAnalyst);

  // Presentation-only gate for the Hunts tab affordance. Mirrors the server's
  // HuntsVisible decision (Praetorian OR (flag AND analyst/admin role)) so a
  // customer who would only ever see an empty hunt surface never gets a
  // dead-end Hunts tab. NOT a data gate: useHunts still calls the server, which
  // remains the source of truth — a wrong-positive here only exposes the
  // already-empty view the server returns; a wrong-negative just hides a tab
  // that would have been empty anyway. Same shape as canUseAgentMode.
  const canViewHunts = isPraetorianUser || (huntVisibility && isAtLeastAnalyst);

  // Single source of truth for the active chat conversation's mode + agent,
  // keyed by the conversation it describes. Because convState.convId is derived
  // from the same conversationId it must agree with, mode can NEVER lag
  // conversationId across a router transition (the torn-state class is
  // unrepresentable). See the adjust-during-render block below.
  const [convState, setConvState] = useState<ConvState>(() =>
    deriveConvState(conversationId, { canUseAgentMode })
  );

  // 'auto' is an APP VIEW (the Hunt section), not a per-conversation mode.
  // Orthogonal flag so it can never be persisted against a chat conversation.
  const [isHuntView, setIsHuntView] = useState(false);

  // Adjust state during render (React's supported store-prev-render technique):
  // re-derive synchronously when the URL conversation changes, so the set makes
  // the guard false and convergence happens in the same commit (no extra paint,
  // no tear). The second clause makes the agent-unavailable downgrade reactive
  // (when canUseAgentMode flips false, re-derive heals 'agent' -> 'query').
  if (
    conversationId !== convState.convId ||
    (convState.mode === 'agent' && !canUseAgentMode)
  ) {
    setConvState(deriveConvState(conversationId, { canUseAgentMode }));
  }

  // Composite read union for back-compat child props/readers. Writers never use
  // setMode; they call the write-through helpers below.
  const mode: AIAssistantMode = isHuntView ? 'auto' : convState.mode;
  const activeAgent = convState.agent;

  // Deliberate chat-mode change (user toggle / slash command / welcome). Updates
  // the single store AND persists by id atomically. Leaves the hunt view.
  const applyChatMode = (next: 'query' | 'agent') => {
    setIsHuntView(false);
    setConvState(s => ({
      ...s,
      mode: next,
      agent: clampAgentToMode(next, s.agent),
    }));
    if (conversationId) {
      saveConversationMode(
        conversationId,
        next,
        clampAgentToMode(next, convState.agent) ?? undefined
      );
    }
  };

  // Deliberate active-agent change (@agent / /agent:). Setting an agent implies
  // agent mode. Persists by id when an agent is set against a conversation.
  const applyActiveAgent = (agentName: string | null) => {
    setConvState(s => ({
      ...s,
      agent: agentName,
      mode: agentName ? 'agent' : s.mode,
    }));
    if (conversationId && agentName) {
      saveConversationMode(conversationId, 'agent', agentName);
    }
  };

  const {
    conversations,
    messages,
    messagesLoading,
    pendingMessages: workerPendingMessages,
    hasMoreConversations,
    isLoadingMoreConversations,
    loadMoreConversations,
    sendMessage: sendMessageWorker,
    createConversation: createConversationWorkerRaw,
    compactConversation: compactConversationWorker,
    stopConversation: stopConversationWorker,
  } = useChatWorker(conversationId || undefined);

  const {
    setMessageHistory,
    setHistoryIndex,
    setSavedMessage,
    handleHistoryNavigation,
  } = useMessageHistory({ newMessage, setNewMessage });

  const { mutate: createHunt, isPending: isHuntPending } = useCreateHunt();

  // The budget gate (useHannibalSummonGate) owns onBudgetExhausted but is
  // declared AFTER useHuntMode (it needs huntMode.setPendingMission for the
  // pre-flight gate). Forward the gate's END-guard callback into useHuntMode via
  // a ref to break that declaration-order cycle — the 402 fires at event time,
  // long after both hooks have run, so the ref is always populated by then.
  const onBudgetExhaustedRef = useRef<() => void>(() => {});
  const huntMode = useHuntMode({
    setNewMessage,
    agents,
    createHunt,
    isHuntPending,
    onBudgetExhausted: () => onBudgetExhaustedRef.current(),
  });

  // BEGINNING gate + budget state, extracted to keep this file a composition
  // root. Fix A (no stale-figure flash on the 402 path) lives inside the hook.
  const {
    handleSummonHannibal,
    onBudgetExhausted,
    budgetExhaustedOpen,
    setBudgetExhaustedOpen,
    aiBudget,
    budgetRefreshing,
  } = useHannibalSummonGate({
    setPendingMission: huntMode.setPendingMission,
  });
  // Forward the gate's onBudgetExhausted into useHuntMode (via onBudgetExhaustedRef
  // above), breaking the hook declaration-order cycle. The 402 fires at event time —
  // long after first commit — so a post-commit effect is safe and avoids a
  // render-phase ref write.
  useEffect(() => {
    onBudgetExhaustedRef.current = onBudgetExhausted;
  }, [onBudgetExhausted]);

  const {
    showCommandMenu,
    filteredCommands,
    executeCommandIfMatch,
    shouldAutoComplete,
    selectCommand,
    replaceCurrentWord,
    menuType,
  } = useCommandMenu(
    newMessage,
    canUseAgentMode,
    applyChatMode,
    () => setNewMessage(''),
    () => {},
    agents,
    convState.mode,
    (mission: string) => {
      handleSummonHannibal(mission);
    }
  );

  // useMessageSending is called before useConversationManager so that
  // setIsWaitingForResponse is available to thread into the conversation handlers.
  const {
    failedMessage,
    setFailedMessage,
    isWaitingForResponse,
    setIsWaitingForResponse,
    handleSendMessage,
    handleExampleClick,
    createConversationWorker,
  } = useMessageSending({
    newMessage,
    setNewMessage,
    conversationId,
    mode,
    activeAgent,
    applyActiveAgent,
    isInputDisabled: false,
    sendMessageWorker,
    createConversationWorkerRaw,
    executeCommandIfMatch,
    shouldAutoComplete,
    selectCommand,
    filteredCommands,
    setMessageHistory,
    setHistoryIndex,
    setSavedMessage,
    textareaRef,
  });

  // Navigation handlers (handleModeChange, handleCreateConversation,
  // handleSelectConversation) and modeChangeRef live in useConversationManager.
  const {
    conversationPreviews,
    modeFilteredConversations,
    handleDeleteConversation,
    handleModeChange,
    handleCreateConversation,
    handleSelectConversation,
  } = useConversationManager({
    conversations,
    messages,
    conversationId,
    applyChatMode,
    setActiveTab,
    setIsHuntView,
    setIsWaitingForResponse,
    isInputDisabled: false,
    modeChangeRef,
  });

  // By-id lookup for the active conversation. Feeds both selectedConversation
  // (below) and useConversationSwitch's self-heal: a RESTORED id that fetches
  // empty (hasFetchedData === true, data === undefined) is a dead conversation
  // and gets cleared. hasFetchedData (NOT isFetched) is required so we wait out
  // the loading / post-queryClient.clear() windows.
  const {
    data: selectedConversationById,
    hasFetchedData: hasFetchedConversationById,
  } = useGetConversationById(conversationId || undefined);

  const { isLoadingConversation } = useConversationSwitch({
    conversationId,
    conversationExists: selectedConversationById !== undefined,
    hasFetchedConversation: hasFetchedConversationById,
    setActiveTab,
    setIsHuntView,
    isAIDisabledButBypassed,
    disableAIInference,
    summonMessage,
    messages,
    messagesLoading,
    modeChangeRef,
    historySetters: { setMessageHistory, setHistoryIndex, setSavedMessage },
    messagingSetters: { setFailedMessage, setIsWaitingForResponse },
  });

  const {
    agentExecutions,
    expandedAgentCards,
    loadingStatus,
    thinkingStartTime,
    toggleAgentCard,
  } = useAgentExecution({
    messages,
    activeAgent,
    conversationId,
    agents,
  });

  // When Marcus saves an attack graph server-side (save_attack_graph tool),
  // invalidate the ['MY','file'] cache so the Attack Paths tab refetches.
  useAttackGraphSaveInvalidation(messages);

  const {
    showScrollToBottom,
    scrollContainerRef,
    messagesEndRef,
    scrollToBottom,
  } = useScrollBehavior({
    messagesLength: messages.length,
    workerPendingMessagesLength: workerPendingMessages.length,
    conversationId,
  });

  useSummon({
    summonMessage,
    summonMode,
    isAIDisabledButBypassed,
    // Pending-chat write-through: set the mode for the about-to-be-created
    // conversation. No persist here (no id yet); useSummon persists against the
    // freshly-created newId itself. The subsequent setSearchParams(newId) flips
    // conversationId, which the adjust-during-render block re-derives from.
    setPendingChatMode: (next: 'query' | 'agent') => {
      setIsHuntView(false);
      setConvState(s => ({
        ...s,
        mode: next,
        agent: clampAgentToMode(next, s.agent),
      }));
    },
    createConversation: createConversationWorker,
    setSearchParams,
    setFailedMessage,
  });

  const {
    showToolCallsPopover,
    setShowToolCallsPopover,
    showTipsMenu,
    setShowTipsMenu,
  } = usePopoverDismiss();

  const selectedConversation = conversationId
    ? mergeSelectedConversation(
        conversations.find(
          c =>
            c.uuid === conversationId ||
            c.id === conversationId ||
            c.key?.includes(conversationId)
        ),
        selectedConversationById
      )
    : undefined;

  const parentConversationId = selectedConversation?.parent_id;
  const isSubagent = !!parentConversationId && parentConversationId !== 'self';
  const subagentStatus = selectedConversation?.status;

  const isSubagentQueued = isSubagent && subagentStatus === 'queued';
  const isSubagentFinished = isSubagent && subagentStatus === 'idle';
  const subagentAnchorId = isSubagent
    ? parentConversationId
    : conversationId || undefined;
  // Anchor the interaction id-set on the TRUE tree root, not just the
  // immediate parent: viewing a deep descendant (P -> C1 -> G2) must still
  // surface HITL requests from P and P's sibling branches (ENG-4404 / former
  // ENG-4513). useConversationRoot walks parent_id up to the root; it returns
  // the immediate parent immediately and climbs as ancestors resolve
  // (progressive widening — the id-set only ever grows, never flashes narrow).
  const { rootId: rootAnchorId } = useConversationRoot(
    conversationId || undefined,
    selectedConversation
  );
  // Aggregate HITL interactions across the WHOLE conversation tree, anchored on
  // the tree ROOT (rootAnchorId, resolved by useConversationRoot above) + every
  // descendant subagent, depths 1..5 — so a credential request raised by a
  // subagent at ANY depth or on a sibling branch surfaces here, not only in its
  // own child chat (ENG-3976 established depth 1; ENG-4404 generalizes to the
  // full tree and to root-anchoring).
  //
  // Descendants can live in EITHER partition at every level: a plain chat
  // subagent in the user partition (`{account}:{user}`), a hunt / HITL-Romulus
  // subagent in the tenant partition (`{account}` — spawn_agent_sync forces
  // WithHunt/WithPublic). useDescendantConversations owns the discovery: a
  // dual-partition parent_id BFS for chat descendants plus a hunt-family query
  // for tenant subtrees, unioned and deduped into ONE anchor-reachable list.
  // This is load-bearing: useInteractions subscribes to WS interaction rows on
  // BOTH partitions but drops any whose conversationId is absent from this
  // id-set, and its fetch fan-out only queries ids in the set — so a descendant
  // at ANY depth (1..5) missing here means its credential request never
  // surfaces (the primary case this feature exists for).
  //
  // tenantOnlyDescendantIds generalizes the former tenantOnlyChildIds: the
  // active-chat loaders (useChatWorker / useGetConversationById) fetch the user
  // partition only, so navigating the chat into a tenant-only descendant (at
  // any depth) yields a stuck "Loading conversation…" that 404s on send. We
  // keep those VISIBLE in the roster but surface their request in-place instead
  // of navigating in (see the TraceDrawer onNavigate handler below).
  // userDescendantIds is intentionally not destructured here: user-partition
  // descendants at any depth are loadable by the user-scoped chat loaders, so
  // only the tenant-only set gates navigation (see the onNavigate guard below).
  const { descendants: allDescendantConversations, tenantOnlyDescendantIds } =
    useDescendantConversations(rootAnchorId);

  const descendantInteractionIds = allDescendantConversations
    .map(c => c.uuid || c.id || '')
    .filter(Boolean);
  const interactionConversationIds = rootAnchorId
    ? [rootAnchorId, ...descendantInteractionIds]
    : descendantInteractionIds;

  // The parent/anchor conversation object (from the sidebar list), so the
  // credential-request label for a PARENT-owned interaction viewed from inside
  // a subagent resolves the parent's topic instead of the generic
  // "Agent <last8>" fallback. When not inside a subagent the anchor IS the
  // active conversation, which the label helpers special-case first.
  const anchorConversation = subagentAnchorId
    ? conversations.find(
        c =>
          c.uuid === subagentAnchorId ||
          c.id === subagentAnchorId ||
          c.key?.includes(subagentAnchorId)
      )
    : undefined;
  const { pendingInteractions, pendingByConversationId, submitCredential } =
    useInteractions(conversationId || undefined, interactionConversationIds);
  const needsInputConversationIds = new Set(pendingByConversationId.keys());

  // Mirror the latest pendingInteractions so the async credential-submit handler
  // re-checks the CURRENT roster after its in-flight await, not the stale
  // click-time closure. A sibling HITL request that arrives on the WS mid-submit
  // (its render commits before the POST resolves) is therefore seen by the
  // hand-off decision; the closed-over value never reflects it (Codex P2).
  // Render-phase mirror write, same pattern as useInteractions.ts.
  const pendingInteractionsRef = useRef(pendingInteractions);
  // Render-phase mirror write; the ref is only READ in the async submit
  // callback, never during render output (same pattern as useInteractions.ts).
  // eslint-disable-next-line react-hooks/refs
  pendingInteractionsRef.current = pendingInteractions;

  // Credential-requests drawer (B+C hybrid): open/selection state is local and
  // prop-drilled — no new Zustand store. The trace drawer is closed on open so
  // the two right-side surfaces never stack.
  const [credDrawerOpen, setCredDrawerOpen] = useState(false);
  const [selectedRequestKey, setSelectedRequestKey] = useState<string | null>(
    null
  );

  const { data: traces } = useGetConversationTraces(
    selectedConversation?.trace_id
  );

  useTraceDrawerAutoOpen({
    traceId: selectedConversation?.trace_id,
    traces,
    enabled: !!conversationId,
  });

  const openTraceDrawer = useTraceDrawerStore(s => s.open);
  const closeTraceDrawer = useTraceDrawerStore(s => s.close);
  const traceDrawerOpen = useTraceDrawerStore(s => s.isOpen);

  // Open the credential-requests drawer to a specific request (from the toast
  // "Open" action or the HeaderBar opener), closing the trace drawer so the two
  // right-side surfaces never stack.
  const openCredentialRequests = (requestKey: string | null) => {
    setSelectedRequestKey(requestKey);
    setCredDrawerOpen(true);
    closeTraceDrawer();
  };

  // Enforce the "two right-side drawers never stack" invariant SYMMETRICALLY:
  // openCredentialRequests already closes the trace drawer, and this closes the
  // credential drawer whenever the trace drawer opens via ANY path (HeaderBar,
  // /traces command, workflow auto-open). Edge-triggered on the closed→open
  // transition so it never fights a user reopening the credential drawer while
  // the trace drawer is already open — whichever drawer opens last wins.
  const prevTraceDrawerOpenRef = React.useRef(traceDrawerOpen);
  React.useEffect(() => {
    if (traceDrawerOpen && !prevTraceDrawerOpenRef.current) {
      setCredDrawerOpen(false);
    }
    prevTraceDrawerOpenRef.current = traceDrawerOpen;
  }, [traceDrawerOpen]);

  // Auto-open the credential drawer on the first none→pending transition (and on
  // mount when already pending) so a deadline-bound credential request can't be
  // missed behind a transient toast. Routes through openCredentialRequests, which
  // also closes the trace drawer, preserving the "two right-side drawers never
  // stack" invariant. Fires at most once per request key and never re-opens a key
  // the user manually dismissed while it is still pending.
  useAutoOpenCredentialDrawer(pendingInteractions, credDrawerOpen, key =>
    openCredentialRequests(key)
  );

  // Once the request shown as a form leaves pending — the operator answers it or
  // it hits its ~10-minute expiry — advance to the next oldest pending request in
  // place, so a second concurrent HITL prompt isn't stranded on the passive path
  // within its 10-minute window. Never advances while the selected request is
  // still pending (still answerable), and never reopens after a manual close.
  useAdvanceCredentialSelection(
    pendingInteractions,
    credDrawerOpen,
    selectedRequestKey,
    setSelectedRequestKey
  );

  // Active-notify: one-shot sonner toast per new request, with an "Open" action
  // that deep-links into the credential drawer (seen-key ref prevents re-fire).
  useInteractionToast(
    pendingInteractions,
    conversationIdForLabel => {
      // Self-referential: the active/parent conversation's OWN request. Match
      // the drawer's "This conversation" label instead of mislabeling it as a
      // subagent's.
      if (conversationIdForLabel === conversationId) return 'This conversation';
      const child = allDescendantConversations.find(
        c => (c.uuid || c.id) === conversationIdForLabel
      );
      if (child) {
        return parseSubagentTopic(child.topic, conversationIdForLabel)
          .agentLabel;
      }
      // A parent/anchor-owned request seen from inside a subagent: resolve the
      // parent's topic rather than falling through to "Agent <last8>".
      if (conversationIdForLabel === subagentAnchorId && anchorConversation) {
        return parseSubagentTopic(
          anchorConversation.topic,
          conversationIdForLabel
        ).agentLabel;
      }
      return parseSubagentTopic(undefined, conversationIdForLabel).agentLabel;
    },
    key => openCredentialRequests(key)
  );

  const activeHuntUuid = useAutoDrawerStore(s => s.huntUuid);

  React.useEffect(() => {
    return () => {
      closeTraceDrawer();
    };
  }, [closeTraceDrawer]);

  const isInputDisabled =
    aiAssistantLocked ||
    isAIDisabledButBypassed ||
    isSubagentQueued ||
    isSubagentFinished;

  // Voice input (ENG-4572): the ONE hook instance for the whole Marcus
  // surface — InputForm and ChatWelcome both consume it, so keyboard
  // push-to-talk and the mic buttons can never run two competing recognition
  // sessions. Keyboard PTT is additionally gated inside the hook on the
  // `global` hotkey scope (off while any overlay is open). `!isHuntView` is
  // the "a Marcus composer is rendered" predicate: with a conversationId,
  // InputForm always renders; without one, ChatWelcome renders its composer
  // iff mode !== 'auto', i.e. iff !isHuntView — on the Hannibal/auto view no
  // composer exists to receive the transcript, so keyboard PTT stays off.
  const {
    isRecording,
    isFinalizing,
    isSupported: isVoiceSupported,
    toggleRecording,
    pressStart: recordPressStart,
    pressEnd: recordPressEnd,
    cancel: cancelVoiceInput,
  } = useVoiceInput({
    setNewMessage,
    enabled: !isInputDisabled && !isHuntView,
  });

  // Background capability jobs (status JQ/JR) spawned by the agent keep running
  // after the conversation planner goes idle. Surface them so the stop button
  // stays available. Polling is gated on conversationId inside the hook (no
  // worker runs when no conversation is selected). Cancellation is owned by the
  // backend stop endpoint (see handleStop), not the client.
  const {
    hasActiveJobs,
    jobs: activeJobs,
    isLoading: isLoadingActiveJobs,
  } = useConversationActiveJobs(conversationId || undefined);

  // Planner-only signal: the conversation planner is doing work when we're
  // awaiting a first response, the message stream shows it thinking, or the
  // conversation row is still active. This intentionally EXCLUDES hasActiveJobs
  // so a running planner (Send hidden, Stop shown) is decoupled from background
  // jobs (Send stays available).
  const plannerSignal =
    isWaitingForResponse ||
    loadingStatus !== '' ||
    selectedConversation?.status === 'active';

  // Staleness cutoff: if the planner signal stays on for >90s with no new
  // message activity, treat it as wedged (e.g. a dropped active->idle MODIFY
  // that never reached the sidebar list row) and unblock the gate. The activity
  // key is message identity + count; message rows are write-once, so planner
  // progress always arrives as NEW rows and re-arms the window. Tradeoff: a
  // genuinely quiet >90s step unblocks Enter early — the backend inboxes
  // messages while status=='active'; showBackgroundStop still covers job runs.
  const lastMessageKey = messages.length
    ? `${messages.length}:${messages[messages.length - 1].key}`
    : '';
  const plannerStale = usePlannerStaleCutoff(plannerSignal, lastMessageKey);
  const isPlannerRunning = plannerSignal && !plannerStale;

  // Background capability jobs keep the Stop available even when the planner is
  // idle — without hiding Send. Gated on !isPlannerRunning so the planner-running
  // case dominates and we never render a double Stop.
  const showBackgroundStop = !isPlannerRunning && hasActiveJobs;

  const [isStopping, setIsStopping] = React.useState(false);

  // Clear the transient "stopping" state once ALL run signals are gone (planner
  // idle AND no background jobs), otherwise a stale "stopping" pulse can persist.
  React.useEffect(() => {
    if (!isPlannerRunning && !hasActiveJobs) setIsStopping(false);
  }, [isPlannerRunning, hasActiveJobs]);

  // Stop the running agent. The backend `POST /planner/stop` endpoint both
  // writes the planner stop signal AND cancels every non-terminal background
  // job for the conversation server-side (`cancelSubagentJobs` in
  // backend/pkg/handler/handlers/conversation/stop.go) — the job cancellation
  // runs even when the planner is already idle, so no separate client-side
  // cancel is needed. The backend finalizes its partial result and goes idle;
  // the user's next message re-invokes the planner fresh.
  const handleStop = async () => {
    if (!conversationId || isStopping) return;
    setIsStopping(true);
    try {
      await stopConversationWorker(conversationId);
    } catch (err) {
      console.error('Failed to stop conversation', err);
      setIsStopping(false);
    }
  };

  // Compaction: ask the backend to summarize the current conversation with a
  // large model and seed a new conversation with that summary. The backend
  // returns the new (empty) conversation immediately; the summary message
  // arrives later via the standard WebSocket fan-out.
  const handleCompactConversation = async () => {
    if (!conversationId || isInputDisabled) return;
    try {
      const conv = await compactConversationWorker(conversationId);
      const newId =
        conv.uuid || conv.id || (conv.key || '').replace('#conversation#', '');
      if (!newId) return;
      setIsWaitingForResponse(true);
      // Leave the hunt view; the new conversation's mode derives from its
      // conversationId once the transition lands. Never write against the
      // SOURCE conversation (it keeps its real mode) — there is no setMode here,
      // so the transient (source, 'query') corruption is impossible.
      setIsHuntView(false);
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('conversationId', newId);
        return newParams;
      });
    } catch (err) {
      console.error('Failed to compact conversation', err);
    }
  };

  // Starter-hint click during the PTT release flush (ENG-4584): an explicit
  // hint click is the user choosing fixed content over the dictation, so the
  // pending straggler flush is DISCARDED before the hint runs — otherwise it
  // commits into the NEW conversation's composer as a headless fragment.
  // Mirrors round 4's "new press wins the flush". Gated on `isFinalizing`
  // only: a live recording during a hint click is visible and user-controlled.
  const handleExampleClickSettled = (
    query: string,
    sendImmediately?: boolean
  ) => {
    if (isFinalizing) cancelVoiceInput();
    return handleExampleClick(query, sendImmediately);
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(prev => !prev);
  };

  const handleTracesCommand = () => {
    if (selectedConversation?.trace_id || hasActiveJobs) {
      openTraceDrawer();
    }
  };

  // After the operator submits a credential, if it was the LAST pending request
  // in the tree, hand off from the (now-empty) credential drawer to the workflow
  // (trace) drawer instead of lingering on "No pending credential requests."
  // (ENG-3976). If other requests remain pending, do nothing —
  // useAdvanceCredentialSelection advances the form in place.
  const handleCredentialSubmit: SubmitCredential = async (
    convId,
    reqId,
    inputs
  ) => {
    // Let submitCredential throw on failure so InteractionPromptBody's catch still
    // toasts + re-enables the form; never close or hand off on a failed submit.
    await submitCredential(convId, reqId, inputs);
    // Fire only when the operator answered the LAST pending request. Other pending
    // requests → do nothing; useAdvanceCredentialSelection advances the form.
    if (!isLastPendingRequest(pendingInteractionsRef.current, convId, reqId))
      return;
    setCredDrawerOpen(false);
    // Guard the hand-off open: TraceDrawer's reconcile self-closes (pickDefaultTab
    // → null) when it has no Jobs/Workflow/Subagents content, which would flash the
    // drawer open then shut. Mirror TraceDrawer's own content check exactly —
    // hasJobs there is countLedgerJobs(jobs).total > 0, which counts COMPLETED
    // jobs too, not just the active (JQ/JR) ones hasActiveJobs tracks — so the
    // hand-off fires in precisely the cases the drawer has something to show.
    const hasTraceContent =
      !!selectedConversation?.trace_id ||
      countLedgerJobs(activeJobs).total > 0 ||
      allDescendantConversations.length > 0;
    if (hasTraceContent) openTraceDrawer();
  };

  if (featureLockLoading) {
    return <Spinner centered />;
  }

  return (
    <div
      className="relative flex h-full min-w-0 overflow-hidden"
      data-tutorial="ai-assistant"
    >
      <FeatureLock
        locked={aiAssistantLocked}
        title={lockTitle}
        description={lockDescription}
        action={createContactSupportButton(featureName)}
      >
        <Sidebar
          isSidebarCollapsed={isSidebarCollapsed}
          toggleSidebar={toggleSidebar}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          mode={mode}
          isAIDisabledButBypassed={isAIDisabledButBypassed}
          isInputDisabled={isInputDisabled}
          conversationPreviews={conversationPreviews}
          modeFilteredConversations={modeFilteredConversations}
          selectedConversationId={conversationId}
          onCreateConversation={handleCreateConversation}
          onSelectConversation={handleSelectConversation}
          onDeleteConversation={handleDeleteConversation}
          onExampleClick={handleExampleClickSettled}
          onModeChange={handleModeChange}
          showHunts={!!canViewHunts}
          hunts={hunts}
          activeHuntUuid={activeHuntUuid}
          onNewHunt={() => handleSummonHannibal()}
          hasMoreConversations={!!hasMoreConversations}
          isLoadingMoreConversations={!!isLoadingMoreConversations}
          onLoadMoreConversations={loadMoreConversations}
        />

        <div className="relative flex min-w-0 flex-1 flex-col">
          {conversationId && (
            <HeaderBar
              conversationId={conversationId}
              messages={messages}
              mode={mode}
              isWaitingForResponse={isWaitingForResponse}
              isPlannerRunning={isPlannerRunning}
              isThinking={loadingStatus !== ''}
              selectedConversation={selectedConversation}
              traces={traces}
              childConversations={allDescendantConversations}
              hasActiveJobs={hasActiveJobs}
              loadingStatus={loadingStatus}
              agentExecutions={agentExecutions}
              expandedAgentCards={expandedAgentCards}
              thinkingStartTime={thinkingStartTime}
              showToolCallsPopover={showToolCallsPopover}
              setShowToolCallsPopover={setShowToolCallsPopover}
              onTracesCommand={handleTracesCommand}
              onSetPendingMission={mission => handleSummonHannibal(mission)}
              pendingCredentialCount={pendingInteractions.length}
              needsInputConversationIds={needsInputConversationIds}
              onOpenCredentialRequests={() => openCredentialRequests(null)}
            />
          )}

          {conversationId ? (
            <MessageArea
              conversationId={conversationId}
              messages={messages}
              workerPendingMessages={workerPendingMessages}
              isLoadingConversation={isLoadingConversation}
              failedMessage={failedMessage}
              loadingStatus={loadingStatus}
              isSubagent={isSubagent}
              parentConversationId={parentConversationId}
              selectedConversation={selectedConversation}
              showScrollToBottom={showScrollToBottom}
              scrollContainerRef={scrollContainerRef}
              messagesEndRef={messagesEndRef}
              onScrollToBottom={scrollToBottom}
              agentExecutions={agentExecutions}
              expandedAgentCards={expandedAgentCards}
              toggleAgentCard={toggleAgentCard}
              onCreateConversation={handleCreateConversation}
              onCompactConversation={handleCompactConversation}
            />
          ) : (
            <WelcomeView
              mode={mode}
              canUseAgentMode={canUseAgentMode}
              canViewHunts={canViewHunts}
              agents={agents}
              isInputDisabled={isInputDisabled}
              isAIDisabledButBypassed={isAIDisabledButBypassed}
              onExampleClick={handleExampleClickSettled}
              onModeChange={handleModeChange}
              setNewMessage={setNewMessage}
              newMessage={newMessage}
              isRecording={isRecording}
              isFinalizing={isFinalizing}
              isVoiceSupported={isVoiceSupported}
              onToggleRecording={toggleRecording}
              onRecordPressStart={recordPressStart}
              onRecordPressEnd={recordPressEnd}
              createConversationWorker={createConversationWorker}
              onSetMode={applyChatMode}
              onSetPendingMission={handleSummonHannibal}
              setFailedMessage={setFailedMessage}
            />
          )}

          {/* Warning banner when AI inference is disabled but user bypasses lock */}
          {isAIDisabledButBypassed && conversationId && (
            <div className="mx-6 mb-2 flex items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/10 px-4 py-2.5">
              <ExclamationTriangleIcon className="size-5 shrink-0 text-amber-500" />
              <p className="text-chariot-text-secondary text-sm">
                LLM functionality disabled. Administrators can enable AI
                Inference under Settings {'->'} Organization.
              </p>
            </div>
          )}

          {conversationId && (
            <InputForm
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              mode={mode}
              onModeChange={handleModeChange}
              isInputDisabled={isInputDisabled}
              isWaitingForResponse={isWaitingForResponse}
              isPlannerRunning={isPlannerRunning}
              showBackgroundStop={showBackgroundStop}
              isStopping={isStopping}
              onStop={handleStop}
              isRecording={isRecording}
              isFinalizing={isFinalizing}
              isVoiceSupported={isVoiceSupported}
              isAIDisabledButBypassed={isAIDisabledButBypassed}
              canUseAgentMode={canUseAgentMode}
              canViewHunts={canViewHunts}
              isSubagent={isSubagent}
              isSubagentFinished={isSubagentFinished}
              isSubagentQueued={isSubagentQueued}
              activeAgent={activeAgent}
              textareaRef={textareaRef}
              commandMenuRef={commandMenuRef}
              showCommandMenu={showCommandMenu}
              filteredCommands={filteredCommands}
              menuType={menuType}
              showTipsMenu={showTipsMenu}
              setShowTipsMenu={setShowTipsMenu}
              onSubmit={handleSendMessage}
              onToggleRecording={toggleRecording}
              onRecordPressStart={recordPressStart}
              onRecordPressEnd={recordPressEnd}
              onSelectCommand={selectCommand}
              onReplaceCurrentWord={replaceCurrentWord}
              onHistoryNavigation={handleHistoryNavigation}
              maxTextareaHeight={MAX_TEXTAREA_HEIGHT}
            />
          )}

          <LaunchHannibalDialog
            open={huntMode.pendingMission !== null}
            mission={huntMode.pendingMission ?? ''}
            onLaunch={huntMode.handleHannibalLaunch}
            onCancel={huntMode.handleHannibalCancel}
            isPending={huntMode.isHuntPending}
          />

          <BudgetExhaustedModal
            open={budgetExhaustedOpen}
            onClose={() => setBudgetExhaustedOpen(false)}
            currentUsage={aiBudget?.current_usage}
            maxLimit={aiBudget?.max_limit}
            resetDuration={aiBudget?.reset_duration}
            calendarAligned={aiBudget?.calendar_aligned}
            refreshing={budgetRefreshing}
          />

          {isHuntView ? (
            <HuntStatusDrawer />
          ) : (
            <TraceDrawer
              traceId={selectedConversation?.trace_id}
              jobs={activeJobs}
              isLoadingJobs={isLoadingActiveJobs}
              hasActiveJobs={hasActiveJobs}
              childConversations={allDescendantConversations}
              currentConversationId={conversationId ?? undefined}
              parentConversationId={
                isSubagent ? parentConversationId : undefined
              }
              onNavigateToConversation={id => {
                // Tenant-partition subagent conversations aren't loadable from
                // the user partition (the chat loaders are user-scoped), so
                // navigating in would strand the user on a stuck/404 view. Per
                // ENG-3976 we surface the request in-place: open the credential
                // drawer preselected to this conversation's pending request (or
                // its roster when it has none) instead of navigating the chat.
                if (tenantOnlyDescendantIds.has(id)) {
                  openCredentialRequests(
                    pendingByConversationId.get(id)?.key ?? null
                  );
                  return;
                }
                setSearchParams(prev => {
                  const newParams = new URLSearchParams(prev);
                  newParams.set('conversationId', id);
                  return newParams;
                });
              }}
              needsInputConversationIds={needsInputConversationIds}
            />
          )}

          {conversationId && (
            <CredentialRequestsDrawer
              open={credDrawerOpen}
              onClose={() => setCredDrawerOpen(false)}
              pendingInteractions={pendingInteractions}
              childConversations={allDescendantConversations}
              anchorConversation={anchorConversation}
              activeConversationId={conversationId}
              selectedRequestKey={selectedRequestKey}
              onSelectRequest={setSelectedRequestKey}
              onSubmit={handleCredentialSubmit}
            />
          )}
        </div>
      </FeatureLock>
    </div>
  );
}

export default AIAssistant;
